/**
 * CampaignBuilder - Stories 24.48-24.52
 * Step-by-step campaign creation wizard
 */

import React, { useState } from 'react';
import {
  Target, Users, Calendar, FileText, Sparkles, Settings,
  ChevronRight, ChevronLeft, Check, AlertCircle, Lightbulb,
  Plus, Trash2, Eye, Save, Zap, ArrowRight
} from 'lucide-react';

export interface CampaignStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  isComplete: boolean;
  isOptional?: boolean;
}

export interface CampaignData {
  name: string;
  objective: string;
  targetAudience: string[];
  contentTypes: string[];
  startDate: string;
  endDate: string;
  channels: string[];
  budget?: string;
  kpis: string[];
}

interface CampaignBuilderProps {
  initialData?: Partial<CampaignData>;
  onSave: (data: CampaignData) => void;
  onCancel?: () => void;
  onPreview?: (data: CampaignData) => void;
  aiSuggestionsEnabled?: boolean;
  onAISuggest?: (field: string) => Promise<string[]>;
  className?: string;
}

const defaultSteps: CampaignStep[] = [
  { id: 'basics', title: 'Campaign Basics', description: 'Name and objective', icon: Target, isComplete: false },
  { id: 'audience', title: 'Target Audience', description: 'Define who you want to reach', icon: Users, isComplete: false },
  { id: 'content', title: 'Content Types', description: 'Choose content to create', icon: FileText, isComplete: false },
  { id: 'schedule', title: 'Schedule', description: 'Set timeline and milestones', icon: Calendar, isComplete: false },
  { id: 'settings', title: 'Settings', description: 'Budget and KPIs', icon: Settings, isComplete: false, isOptional: true }
];

const audienceOptions = [
  'Enterprise Decision Makers', 'IT Directors', 'C-Suite Executives',
  'Technical Engineers', 'Product Managers', 'Marketing Leaders',
  'Small Business Owners', 'Developers', 'Analysts'
];

const contentTypeOptions = [
  // Standard content types
  'Blog Posts', 'Case Studies', 'Whitepapers', 'Email Sequences',
  'Social Media Posts', 'Landing Pages', 'Video Scripts',
  'Webinar Content', 'Sales Decks', 'Technical Docs',
  // Tier 1 ABM Specializations
  'ABM Executive Emails', 'ABM Champion Nurture', 'ABM Multi-Stakeholder',
  'ABM Microsites', 'ABM ROI Calculators', 'Business Case Documents',
  // Tier 1 Sales Enablement
  'Battle Cards', 'Stakeholder One-Pagers', 'Pitch Decks',
  'Champion Pitch Guides', 'Partner Messaging',
  // Tier 1 SMB
  'SMB Conversion Emails', 'SMB Solution Pages', 'Pricing Trigger Emails'
];

const channelOptions = [
  'Website', 'Email', 'LinkedIn', 'Twitter', 'Facebook',
  'YouTube', 'Webinars', 'Events', 'Direct Sales'
];

const kpiOptions = [
  'Lead Generation', 'Website Traffic', 'Engagement Rate',
  'Conversion Rate', 'Brand Awareness', 'Pipeline Value',
  'Customer Acquisition', 'Content Downloads', 'Demo Requests'
];

export const CampaignBuilder: React.FC<CampaignBuilderProps> = ({
  initialData,
  onSave,
  onCancel,
  onPreview,
  aiSuggestionsEnabled = true,
  onAISuggest,
  className = ''
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState(defaultSteps);
  const [data, setData] = useState<CampaignData>({
    name: initialData?.name || '',
    objective: initialData?.objective || '',
    targetAudience: initialData?.targetAudience || [],
    contentTypes: initialData?.contentTypes || [],
    startDate: initialData?.startDate || '',
    endDate: initialData?.endDate || '',
    channels: initialData?.channels || [],
    budget: initialData?.budget || '',
    kpis: initialData?.kpis || []
  });
  const [aiSuggestions, setAISuggestions] = useState<string[]>([]);
  const [loadingAI, setLoadingAI] = useState(false);

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const completedSteps = steps.filter(s => s.isComplete).length;
  const progress = (completedSteps / steps.length) * 100;

  const updateData = (field: keyof CampaignData, value: string | number | boolean | string[]) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field: keyof CampaignData, item: string) => {
    setData(prev => {
      const arr = prev[field] as string[];
      return {
        ...prev,
        [field]: arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item]
      };
    });
  };

  const validateStep = (stepId: string): boolean => {
    switch (stepId) {
      case 'basics': return data.name.length > 0 && data.objective.length > 0;
      case 'audience': return data.targetAudience.length > 0;
      case 'content': return data.contentTypes.length > 0;
      case 'schedule': return data.startDate.length > 0 && data.endDate.length > 0;
      case 'settings': return true; // Optional
      default: return false;
    }
  };

  const goNext = () => {
    // Mark current step as complete if valid
    const isValid = validateStep(step.id);
    setSteps(prev => prev.map((s, i) => 
      i === currentStep ? { ...s, isComplete: isValid } : s
    ));

    if (isLast) {
      onSave(data);
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (!isFirst) setCurrentStep(prev => prev - 1);
  };

  const fetchAISuggestions = async (field: string) => {
    if (!onAISuggest) return;
    setLoadingAI(true);
    try {
      const suggestions = await onAISuggest(field);
      setAISuggestions(suggestions);
    } finally {
      setLoadingAI(false);
    }
  };

  const renderStepContent = () => {
    switch (step.id) {
      case 'basics':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
              <input
                type="text"
                value={data.name}
                onChange={e => updateData('name', e.target.value)}
                placeholder="e.g., Q1 Product Launch Campaign"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Objective *</label>
              <textarea
                value={data.objective}
                onChange={e => updateData('objective', e.target.value)}
                placeholder="What do you want to achieve with this campaign?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg h-24 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {aiSuggestionsEnabled && (
                <button
                  onClick={() => fetchAISuggestions('objective')}
                  className="mt-2 flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700"
                >
                  <Sparkles className="w-3 h-3" />
                  Suggest objectives
                </button>
              )}
            </div>
          </div>
        );

      case 'audience':
        return (
          <div>
            <p className="text-sm text-gray-600 mb-4">Select your target audience segments</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {audienceOptions.map(audience => (
                <button
                  key={audience}
                  onClick={() => toggleArrayItem('targetAudience', audience)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    data.targetAudience.includes(audience)
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {data.targetAudience.includes(audience) && <Check className="w-4 h-4" />}
                    <span className="text-sm">{audience}</span>
                  </div>
                </button>
              ))}
            </div>
            <p className="mt-4 text-sm text-gray-500">
              {data.targetAudience.length} selected
            </p>
          </div>
        );

      case 'content':
        return (
          <div>
            <p className="text-sm text-gray-600 mb-4">What content types will you create?</p>
            <div className="grid grid-cols-2 gap-2">
              {contentTypeOptions.map(type => (
                <button
                  key={type}
                  onClick={() => toggleArrayItem('contentTypes', type)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    data.contentTypes.includes(type)
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {data.contentTypes.includes(type) && <Check className="w-4 h-4" />}
                    <span className="text-sm">{type}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Distribution Channels</h4>
              <div className="flex flex-wrap gap-2">
                {channelOptions.map(channel => (
                  <button
                    key={channel}
                    onClick={() => toggleArrayItem('channels', channel)}
                    className={`px-3 py-1 rounded-full text-sm transition-all ${
                      data.channels.includes(channel)
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {channel}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <input
                  type="date"
                  value={data.startDate}
                  onChange={e => updateData('startDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <input
                  type="date"
                  value={data.endDate}
                  onChange={e => updateData('endDate', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            {data.startDate && data.endDate && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <Lightbulb className="w-4 h-4 inline mr-2" />
                Campaign duration: {Math.ceil((new Date(data.endDate).getTime() - new Date(data.startDate).getTime()) / (1000 * 60 * 60 * 24))} days
              </div>
            )}
          </div>
        );

      case 'settings':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget (Optional)</label>
              <input
                type="text"
                value={data.budget}
                onChange={e => updateData('budget', e.target.value)}
                placeholder="e.g., $50,000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Key Performance Indicators</label>
              <div className="flex flex-wrap gap-2">
                {kpiOptions.map(kpi => (
                  <button
                    key={kpi}
                    onClick={() => toggleArrayItem('kpis', kpi)}
                    className={`px-3 py-1 rounded-full text-sm transition-all ${
                      data.kpis.includes(kpi)
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {kpi}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
      {/* Header with progress */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">Create Campaign</h2>
          <span className="text-sm text-gray-500">{Math.round(progress)}% complete</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full">
          <div
            className="h-full bg-blue-500 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step navigation */}
      <div className="flex border-b border-gray-200 overflow-x-auto">
        {steps.map((s, idx) => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setCurrentStep(idx)}
              className={`flex-1 min-w-[120px] p-4 flex flex-col items-center gap-1 border-b-2 transition-all ${
                idx === currentStep
                  ? 'border-blue-500 bg-blue-50'
                  : s.isComplete
                  ? 'border-green-500 bg-green-50'
                  : 'border-transparent hover:bg-gray-50'
              }`}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                s.isComplete ? 'bg-green-500 text-white' :
                idx === currentStep ? 'bg-blue-500 text-white' :
                'bg-gray-200 text-gray-500'
              }`}>
                {s.isComplete ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-xs font-medium ${
                idx === currentStep ? 'text-blue-700' : 'text-gray-600'
              }`}>
                {s.title}
              </span>
            </button>
          );
        })}
      </div>

      {/* Step content */}
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800">{step.title}</h3>
          <p className="text-sm text-gray-600">{step.description}</p>
        </div>
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="p-6 border-t border-gray-200 flex items-center justify-between">
        <div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          )}
        </div>
        <div className="flex gap-2">
          {onPreview && (
            <button
              onClick={() => onPreview(data)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>
          )}
          {!isFirst && (
            <button
              onClick={goPrev}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          )}
          <button
            onClick={goNext}
            disabled={!validateStep(step.id) && !step.isOptional}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLast ? (
              <><Save className="w-4 h-4" /> Create Campaign</>
            ) : (
              <>Next <ChevronRight className="w-4 h-4" /></>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignBuilder;
